import { SCREEN_H, SCREEN_W } from "../shared/screen";
import type { AbilityDef, AttackButton, SuperDef } from "../sim";
import { COMBO_EXAMPLES } from "../content/abilities";
import { outlinedText } from "./hud";

/** Кнопки удара: значок DualSense и клавиши обеих раскладок. */
const BUTTONS: Readonly<Record<AttackButton, readonly [string, string, string]>> = {
  lightPunch: ["□", "U", "Num 4"],
  heavyPunch: ["△", "I", "Num 5"],
  lightKick: ["✕", "J", "Num 1"],
  heavyKick: ["○", "K", "Num 2"],
};
/** Направления от лица героя (лицом вправо): стрелка, клавиша игрока 1, клавиша игрока 2. */
const DIRECTIONS: Readonly<Record<string, readonly [string, string, string]>> = {
  F: ["→", "D", "→"],
  B: ["←", "A", "←"],
  D: ["↓", "S", "↓"],
  U: ["↑", "W", "↑"],
};

const ROWS: readonly (readonly [string, string, string, string])[] = [
  ["Ходить, прыжок, присед", "стик или крестовина", "W A S D", "стрелки"],
  ["Лёгкая рука / тяжёлая рука", "□ / △", "U / I", "Num 4 / Num 5"],
  ["Лёгкая нога / тяжёлая нога", "✕ / ○", "J / K", "Num 1 / Num 2"],
  ["Блок (присев — снизу)", "R2", "O", "Num 6"],
  ["Суперприём (полная Ярость)", "L2 + R2", "L + P", "Num 0 + Num 3"],
  ["Сбит на пол — встать в бой", "↑ · → кувырок · удар", "W · D · удар", "↑ · → · удар"],
  ["Пауза", "Options", "Esc", "Esc"],
  ["Войти в бой", "✕", "Пробел", "Enter"],
];

/** Ввод приёма в трёх видах: геймпад, игрок 1, игрок 2. */
function inputOf(ability: AbilityDef): [string, string, string] {
  if (ability.input.taunt) return ["тачпад (или сам, 2 с без ввода)", "T", "Num 7"];
  if (ability.input.grab) return ["L1 вплотную (вырваться — L1)", "H", "Num 9"];
  const parts: [string[], string[], string[]] = [[], [], []];
  for (const direction of ability.input.gesture ?? ability.input.motion ?? [])
    DIRECTIONS[direction]?.forEach((key, index) => parts[index]?.push(key));
  const hold = ability.input.hold ? DIRECTIONS[ability.input.hold] : undefined;
  const button = ability.input.button ? BUTTONS[ability.input.button] : ["", "", ""];
  return [0, 1, 2].map((index) => {
    const prefix = ability.input.air
      ? "в прыжке "
      : ability.input.run
        ? "с бега "
        : ability.input.charge !== undefined
          ? "зажать и отпустить "
          : "";
    const held = hold ? `${hold[index] ?? ""} + ` : "";
    const pair = ability.input.with ? ` + ${BUTTONS[ability.input.with][index] ?? ""}` : "";
    return `${prefix}${parts[index]?.join(" ") ?? ""} ${held}${button[index] ?? ""}${pair}`.replace(/\s+/g, " ").trim();
  }) as [string, string, string];
}

/** Удары ветки дерева комбо по кнопкам подряд: первый — обычный удар кнопки, дальше — по веткам. */
function comboChain(kit: readonly AbilityDef[], buttons: readonly AttackButton[]): AbilityDef[] {
  const [first, ...rest] = buttons;
  const head = kit.find((ability) => ability.input.button === first && isPlainStrike(ability));
  if (!head) return [];
  const chain = [head];
  for (const button of rest) {
    const link = chain[chain.length - 1];
    const id = link?.branches?.[button] ?? (link && button === link.input.button ? link.next : undefined);
    const next = kit.find((ability) => ability.id === id);
    if (!next) break;
    chain.push(next);
  }
  return chain;
}

const isPlainStrike = (ability: AbilityDef): boolean => {
  const { input } = ability;
  return (
    !input.series &&
    !input.hold &&
    !input.motion &&
    !input.air &&
    !input.run &&
    !input.with &&
    input.charge === undefined
  );
};

/** Экран «Управление» в паузе: кнопки и все приёмы героя с вводом под DualSense и клавиатуру. */
export function drawControlsPage(
  context: CanvasRenderingContext2D,
  kit: readonly AbilityDef[],
  move: SuperDef | null,
): void {
  context.fillStyle = "#07060a";
  context.fillRect(80, 40, SCREEN_W - 160, SCREEN_H - 80);
  outlinedText(context, "УПРАВЛЕНИЕ", SCREEN_W / 2, 60, 64, "#ffe2b8");
  const columns = [130, 690, 1060, 1450];
  ["", "DualSense", "Клавиатура, игрок 1", "Клавиатура, игрок 2"].forEach((title, index) => {
    outlinedText(context, title, columns[index] ?? 0, 150, 26, "#ffcf4a", "left");
  });
  ROWS.forEach((row, line) => {
    row.forEach((cell, index) => {
      outlinedText(
        context,
        cell,
        columns[index] ?? 0,
        190 + line * 38,
        24,
        index === 0 ? "#e8e2f0" : "#c8c0d8",
        "left",
        "normal",
      );
    });
  });
  outlinedText(context, "ПРИЁМЫ (стрелки — от лица героя: → к сопернику)", columns[0] ?? 0, 500, 26, "#ffcf4a", "left");
  const moves: (readonly string[])[] = kit
    .filter((ability) => !ability.input.series && !ability.input.grabWhiff)
    .map((ability) => [ability.secret ? `${ability.name} (секрет)` : ability.name, ...inputOf(ability)]);
  // дерево комбо: несколько веток для примера — имена ударов по веткам, ввод — кнопки подряд
  for (const buttons of COMBO_EXAMPLES) {
    const chain = comboChain(kit, buttons);
    if (chain.length < 2) continue;
    moves.push([
      chain.map((ability) => ability.name).join(" → "),
      ...[0, 1, 2].map((index) => buttons.map((button) => BUTTONS[button][index] ?? "").join(" ")),
    ]);
  }
  moves.push(["…и ещё десятки веток комбо: жми удары подряд", "", "", ""]);
  if (move)
    moves.push([
      `${move.name[0] ?? ""}${move.name.slice(1).toLowerCase()} — суперприём`,
      "L2 + R2",
      "L + P",
      "Num 0 + Num 3",
    ]);
  // приёмы — в две колонки: ввод DualSense, за ним имя (клавиши каждой кнопки — в таблице выше); всё влезает в экран
  const perColumn = Math.ceil(moves.length / 2);
  moves.forEach((row, line) => {
    const column = line < perColumn ? 0 : 1;
    const x = 130 + column * 860;
    const y = 540 + (line % perColumn) * 26;
    outlinedText(context, row[1] ?? "", x, y, 19, "#ffe2b8", "left", "normal");
    outlinedText(context, row[0] ?? "", x + 245, y, 19, "#e8e2f0", "left", "normal");
  });
  outlinedText(
    context,
    "✕, Enter или Esc — назад · F — полный экран · F1 — индикатор нажатий",
    SCREEN_W / 2,
    SCREEN_H - 80,
    24,
    "#8a7e96",
    "center",
    "normal",
  );
}
